import {
  DragEvent as ReactDragEvent,
  useMemo,
  useState,
} from "react";
import { Typography } from "@mui/material";
import {
  WorkflowStatusMetaData,
  WorkflowSubStatusMetaData,
} from "@finpay-development/shared-platform/lib/esm/meta-data/models/metaData";
import { PatientEncounterCard } from "@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter-card";
import { StatusColors } from "@finpay-development/shared-platform/lib/esm/shared/enums";
import IocTile from "./ioc-tile";
import {
  applyPatchBodyToCard,
  buildStatusPatchBody,
  getCardWorkflowStatusId,
  getCardWorkflowSubStatusId,
  getColumnLabel,
  getColumnSubStatuses,
  getSubStatusLabel,
  groupCardsByStatusAndSubStatus,
  OTHER_SUBSTATUS_KEY,
  PeStatusPatchBody,
} from "./board-columns";
import { updateIocWorkflowStatus } from "./update-ioc-workflow-status";
import "./ioc-board.scss";

export interface IocBoardCardDisplay {
  title: string;
  statusText: string;
  subtitle: string;
  admissionDate?: string;
  reminderDate?: string;
  pfrTimingRiskPayorRisk?: string;
  riskClassName?: string;
  statusColor: StatusColors;
}

export interface IocBoardProps {
  columns: WorkflowStatusMetaData[];
  cards: PatientEncounterCard[];
  getCardDisplay: (card: PatientEncounterCard) => IocBoardCardDisplay;
  onCardClick: (card: PatientEncounterCard) => void;
  onCardsChange: (cards: PatientEncounterCard[]) => void;
  onStatusError: (message: string) => void;
}

const DRAG_TYPE = "application/x-finpass-ioc-encounter-id";

type DropTargetKey = string;

function dropKey(statusId: number, subStatusId: number): DropTargetKey {
  return `${statusId}:${subStatusId}`;
}

function IocBoard(props: IocBoardProps) {
  const {
    columns,
    cards,
    getCardDisplay,
    onCardClick,
    onCardsChange,
    onStatusError,
  } = props;

  const [draggingEncounterId, setDraggingEncounterId] = useState<number | null>(
    null
  );
  const [dropTargetKey, setDropTargetKey] = useState<DropTargetKey | null>(
    null
  );
  const [pendingEncounterId, setPendingEncounterId] = useState<number | null>(
    null
  );

  const isDragging = draggingEncounterId != null;

  const allowedStatusIds = useMemo(() => {
    return new Set(
      columns
        .map((col) => Number(col.workflowStatusId))
        .filter((id) => Number.isFinite(id) && id > 0)
    );
  }, [columns]);

  const cardsByStatusAndSub = useMemo(
    () => groupCardsByStatusAndSubStatus(cards, allowedStatusIds),
    [cards, allowedStatusIds]
  );

  const findCard = (encounterId: number) =>
    cards.find((c) => Number(c.patientEncounterId) === encounterId);

  const handleDragStart = (
    event: ReactDragEvent,
    card: PatientEncounterCard
  ) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest?.(".tile-title-link")) {
      event.preventDefault();
      return;
    }

    const encounterId = Number(card.patientEncounterId);
    if (!encounterId) {
      event.preventDefault();
      return;
    }

    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(DRAG_TYPE, String(encounterId));
    event.dataTransfer.setData("text/plain", String(encounterId));

    // Defer UI switch so the browser finishes starting the drag before React re-renders.
    window.requestAnimationFrame(() => {
      setDraggingEncounterId(encounterId);
    });
  };

  const handleDragEnd = () => {
    setDraggingEncounterId(null);
    setDropTargetKey(null);
  };

  const handleLandingDragOver = (
    event: ReactDragEvent,
    statusId: number,
    subStatusId: number
  ) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "move";
    const key = dropKey(statusId, subStatusId);
    if (dropTargetKey !== key) {
      setDropTargetKey(key);
    }
  };

  const handleLandingDragLeave = (
    event: ReactDragEvent,
    statusId: number,
    subStatusId: number
  ) => {
    const related = event.relatedTarget as Node | null;
    if (related && event.currentTarget.contains(related)) {
      return;
    }
    const key = dropKey(statusId, subStatusId);
    if (dropTargetKey === key) {
      setDropTargetKey(null);
    }
  };

  const handleLandingDrop = async (
    event: ReactDragEvent,
    targetStatus: WorkflowStatusMetaData,
    targetSubStatus: WorkflowSubStatusMetaData | null
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setDropTargetKey(null);

    const raw =
      event.dataTransfer.getData(DRAG_TYPE) ||
      event.dataTransfer.getData("text/plain");
    const encounterId = Number(raw);
    if (!Number.isFinite(encounterId) || encounterId <= 0) {
      return;
    }

    const card = findCard(encounterId);
    if (!card) {
      return;
    }

    const workFlow: PeStatusPatchBody = buildStatusPatchBody(
      targetStatus,
      targetSubStatus
    );
    const targetStatusId = Number(targetStatus.workflowStatusId);
    const targetSubId = workFlow.workflowSubStatus.workflowSubStatusId;

    const sourceStatusId = getCardWorkflowStatusId(card);
    const sourceSubId = getCardWorkflowSubStatusId(card);
    if (sourceStatusId === targetStatusId && sourceSubId === targetSubId) {
      setDraggingEncounterId(null);
      return;
    }

    const previousCards = cards;
    const optimisticCard = applyPatchBodyToCard(card, workFlow);
    onCardsChange(
      cards.map((c) =>
        Number(c.patientEncounterId) === encounterId ? optimisticCard : c
      )
    );
    setPendingEncounterId(encounterId);
    setDraggingEncounterId(null);

    const result = await updateIocWorkflowStatus({
      patientId: Number(card.patientId),
      encounterId,
      workFlow,
    });

    setPendingEncounterId(null);
    if (!result.ok) {
      onCardsChange(previousCards);
      onStatusError(result.errorMessage || "Failed to update workflow status");
    }
  };

  const handleTitleClick = (card: PatientEncounterCard) => {
    if (pendingEncounterId === Number(card.patientEncounterId)) {
      return;
    }
    onCardClick(card);
  };

  const renderCard = (card: PatientEncounterCard) => {
    const encounterId = Number(card.patientEncounterId);
    const display = getCardDisplay(card);
    const cardIsDragging = draggingEncounterId === encounterId;

    return (
      <div
        key={`${card.patientId}-${encounterId}`}
        className={`patient-records-ioc-board-card${
          cardIsDragging ? " is-dragging" : ""
        }`}
        draggable={pendingEncounterId !== encounterId}
        onDragStart={(e) => handleDragStart(e, card)}
        onDragEnd={handleDragEnd}
      >
        <IocTile
          title={display.title}
          titleTo={card.patientId ? `/patients/${card.patientId}` : undefined}
          statusText={display.statusText}
          subtitle={display.subtitle}
          admissionDate={display.admissionDate}
          reminderDate={display.reminderDate}
          pfrTimingRiskPayorRisk={display.pfrTimingRiskPayorRisk}
          riskClassName={display.riskClassName}
          statusColor={display.statusColor}
          onTitleClick={() => handleTitleClick(card)}
        />
      </div>
    );
  };

  if (columns.length === 0) {
    return (
      <Typography className="mb-4">
        Pre-convert workflow statuses are not loaded yet.
      </Typography>
    );
  }

  return (
    <div
      className={`patient-records-ioc-board${isDragging ? " is-dragging" : ""}`}
    >
      {columns.map((column) => {
        const statusId = Number(column.workflowStatusId);
        const subStatuses = getColumnSubStatuses(column);
        const bySub: Map<number, PatientEncounterCard[]> =
          cardsByStatusAndSub.get(statusId) || new Map();
        const columnCards = [...bySub.values()].flat();

        return (
          <section key={statusId} className="patient-records-ioc-column">
            <header className="column-header">
              <h3 className="column-title">{getColumnLabel(column)}</h3>
            </header>

            <div className="column-body">
              <div
                className={`column-cards${isDragging ? " is-drag-active" : ""}`}
              >
                {columnCards.map(renderCard)}
              </div>

              {isDragging ? (
                <div className="column-landings">
                  {subStatuses.length === 0 ? (
                    <div
                      className={`substatus-landing${
                        dropTargetKey ===
                        dropKey(statusId, OTHER_SUBSTATUS_KEY)
                          ? " is-drop-target"
                          : ""
                      }`}
                      onDragOver={(e) =>
                        handleLandingDragOver(
                          e,
                          statusId,
                          OTHER_SUBSTATUS_KEY
                        )
                      }
                      onDragLeave={(e) =>
                        handleLandingDragLeave(
                          e,
                          statusId,
                          OTHER_SUBSTATUS_KEY
                        )
                      }
                      onDrop={(e) => handleLandingDrop(e, column, null)}
                    />
                  ) : (
                    subStatuses.map((sub) => {
                      const subId = Number(sub.workflowSubStatusId);
                      const label = getSubStatusLabel(sub);
                      const hideLabel =
                        !label || label.trim().toLowerCase() === "none";
                      const isDropTarget =
                        dropTargetKey === dropKey(statusId, subId);

                      return (
                        <div
                          key={subId}
                          className={`substatus-landing${
                            isDropTarget ? " is-drop-target" : ""
                          }`}
                          onDragOver={(e) =>
                            handleLandingDragOver(e, statusId, subId)
                          }
                          onDragLeave={(e) =>
                            handleLandingDragLeave(e, statusId, subId)
                          }
                          onDrop={(e) => handleLandingDrop(e, column, sub)}
                        >
                          {!hideLabel ? (
                            <div className="substatus-landing-header">
                              {label}
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : null}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default IocBoard;
